import axios from 'axios';
import { routePreviewRequest } from './studentPreview';
import { guardSessionRequest, observeSessionError } from './sessionRecovery';
import type { RouteStudentsToStepPayload } from '../types/v2';
import type { StudentReview } from '../types/studentReview';

const getCookie = (name: string): string | null => {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i += 1) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === `${name}=`) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

const apiV2 = axios.create({
  baseURL: '/api/v2',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const goalConferenceApi = {
  path: (section: number, student?: number) => student
    ? `/goals/sections/${section}/students/${student}/conference/`
    : `/goals/my/conference/?section_id=${section}`,
  get: (section: number, student?: number) => apiV2.get(goalConferenceApi.path(section, student)).then(r => r.data),
  save: (section: number, student: number | undefined, input: object) => apiV2.post(goalConferenceApi.path(section, student), input).then(r => r.data),
};

export const goalPlannerApi = {
  context: (section: number, student: number) => apiV2.get(`/goals/sections/${section}/students/${student}/planner/`).then(r => r.data),
  pacing: (section: number, student: number, input: object) => apiV2.post(`/goals/sections/${section}/students/${student}/planner/pacing/`, input).then(r => r.data),
  plan: (section: number, student: number, input: object) => apiV2.post(`/goals/sections/${section}/students/${student}/planner/`, input).then(r => r.data),
  coach: async (section: number, student: number, input: object) => {
    const path = `/goals/sections/${section}/students/${student}/planner/`;
    let result = await apiV2.post(path, { ...input, action: 'coach' }, { timeout: 15000 });
    const deadline = Date.now() + 150000;
    while (result.status === 202) {
      if (Date.now() > deadline) throw new Error('Local coaching took too long. The calculated plan is unchanged.');
      const jobId = result.data.job_id;
      await new Promise(resolve => setTimeout(resolve, 2000));
      result = await apiV2.get(path, { params: { job_id: jobId }, timeout: 15000 });
    }
    return result.data;
  },
};

apiV2.interceptors.request.use(async (config) => {
  routePreviewRequest(config);
  await guardSessionRequest(config);
  if (config.method && config.method.toLowerCase() !== 'get') {
    const existingToken = getCookie('csrftoken');
    if (!existingToken) {
      await axios.get('/api/csrf/', { withCredentials: true });
    }
  }
  const csrfToken = getCookie('csrftoken');
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  if (config.method && config.method.toLowerCase() === 'get') {
    config.headers['Cache-Control'] = 'no-cache';
    config.headers.Pragma = 'no-cache';
  }
  return config;
});

apiV2.interceptors.response.use(
  (response) => response,
  (err) => {
    if (err?.code === 'STUDENT_PREVIEW_READ_ONLY') return Promise.reject(err);
    observeSessionError(err);
    const status = err?.response?.status;
    if (status === 404) {
      // Don't log 404 errors to console - they're often expected
      return Promise.reject(err);
    } else {
      // Axios errors contain config.data (student evidence) and response bodies.
      // Keep diagnostics free of payloads, credentials, and identifying URLs.
      console.error('Classroom API request failed', { status: typeof status === 'number' ? status : null });
      return Promise.reject(err);
    }
  }
);

const getPaginatedResults = async (path: string) => {
  const firstResponse = await apiV2.get(path);
  const firstData = firstResponse?.data;
  if (!Array.isArray(firstData?.results)) {
    return firstResponse;
  }

  const results = [...firstData.results];
  let nextUrl = firstData.next;

  while (nextUrl) {
    const pageResponse = await apiV2.get(nextUrl);
    const pageData = pageResponse?.data;
    if (!Array.isArray(pageData?.results)) {
      break;
    }
    results.push(...pageData.results);
    nextUrl = pageData.next;
  }

  return {
    ...firstResponse,
    data: results,
  };
};

export const sectionsApi = {
  list: () => apiV2.get('/sections/'),
  archiveManager: () => apiV2.get('/sections/archive-manager/'),
  archiveManagerAction: (sectionIds: number[], action: 'archive' | 'restore') =>
    apiV2.post('/sections/archive-manager/', { section_ids: sectionIds, action }),
  create: (data: any) => apiV2.post('/sections/', data),
  update: (sectionId: number, data: any) => apiV2.patch(`/sections/${sectionId}/`, data),
  rolloverPreview: (sectionId: number) => apiV2.get(`/sections/${sectionId}/rollover-preview/`),
  rollover: (sectionId: number, data: any) => apiV2.post(`/sections/${sectionId}/rollover/`, data),
  enroll: (sectionId: number, studentIds: number[]) => apiV2.post(`/sections/${sectionId}/enroll/`, { student_ids: studentIds }),
  students: (sectionId: number) => apiV2.get(`/sections/${sectionId}/students/`),
  joinCode: (sectionId: number, regenerate = false) =>
    apiV2.post(`/sections/${sectionId}/join-code/`, { regenerate }),
  joinByCode: (joinCode: string, gradeLevel?: string) => apiV2.post('/sections/join-by-code/', {
    join_code: joinCode,
    ...(gradeLevel ? { grade_level: gradeLevel } : {}),
  }),
  updateStudentGrade: (sectionId: number, studentId: number, gradeLevel: string) =>
    apiV2.post(`/sections/${sectionId}/student-grade/`, {
      student_id: studentId,
      grade_level: gradeLevel,
    }),
  reusePlans: (sectionId: number, sourceSectionId: number) =>
    apiV2.post(`/sections/${sectionId}/reuse-plans/`, { source_section_id: sourceSectionId }),
  copyLessonPlan: (sectionId: number, sourceSectionId: number, sourcePlanId: number) =>
    apiV2.post(`/sections/${sectionId}/copy-lesson-plan/`, {
      source_section_id: sourceSectionId,
      source_plan_id: sourcePlanId,
    }),
  studentPool: (sectionId: number, params?: { q?: string; grade_level?: string }) =>
    apiV2.get(`/sections/${sectionId}/student-pool/`, { params }),
  assignGroups: (sectionId: number, studentIds: number[], groupName: string) =>
    apiV2.post(`/sections/${sectionId}/assign-groups/`, { student_ids: studentIds, group_name: groupName }),
  withdraw: (sectionId: number, studentIds: number[]) => apiV2.post(`/sections/${sectionId}/withdraw/`, { student_ids: studentIds }),
  importStudents: (sectionId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiV2.post(`/sections/${sectionId}/import-students/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const hooksV2Api = {
  list: () => apiV2.get('/hooks/'),
  get: (id: number) => apiV2.get(`/hooks/${id}/`),
  create: (data: any) => apiV2.post('/hooks/', data),
  update: (id: number, data: any) => apiV2.put(`/hooks/${id}/`, data),
  patch: (id: number, data: any) => apiV2.patch(`/hooks/${id}/`, data),
  delete: (id: number) => apiV2.delete(`/hooks/${id}/`),
  restore: (id: number) => apiV2.post(`/hooks/${id}/restore/`),
  copyWithLessons: (
    id: number,
    data: { title: string; curriculum_year?: string; lesson_plan_ids: number[] },
  ) => apiV2.post(`/hooks/${id}/copy-with-lessons/`, data),
  assign: (id: number, sectionId: number) => apiV2.post(`/hooks/${id}/assign/`, { section_id: sectionId }),
  mastery: (id: number) => apiV2.get(`/hooks/${id}/mastery/`),
  unlockApply: (id: number, studentId: number) => apiV2.post(`/hooks/${id}/unlock-apply/`, { student_id: studentId }),
  aiDraftLesson: (
    id: number,
    data: {
      duration_minutes: number;
      hook_mode: string;
      guidance_mode: 'strict' | 'flexible';
      focus_note?: string;
      continue_from_stage?: string;
    },
  ) => apiV2.post(`/hooks/${id}/ai-draft-lesson/`, data),
};

export const lessonPlansApi = {
  list: () => getPaginatedResults('/lesson-plans/'),
  schedules: (sectionId?: number) => apiV2.get('/lesson-plans/schedules/', {
    params: { section_id: sectionId || undefined },
  }),
  updateSchedule: (lessonPlanId: number, sectionId: number, plannedDate: string | null) => apiV2.post(
    '/lesson-plans/schedules/',
    { lesson_plan_id: lessonPlanId, section_id: sectionId, planned_date: plannedDate },
  ),
  printQueue: (days = 7) => apiV2.get('/lesson-plans/print-queue/', { params: { days } }),
  get: (id: number, params?: { audience?: 'student' }) => apiV2.get(`/lesson-plans/${id}/`, { params }),
  create: (data: any) => apiV2.post('/lesson-plans/', data),
  update: (id: number, data: any) => apiV2.put(`/lesson-plans/${id}/`, data),
  patch: (id: number, data: any) => apiV2.patch(`/lesson-plans/${id}/`, data),
  classAvailability: (id: number) => apiV2.get(`/lesson-plans/${id}/class-availability/`),
  updateClassAvailability: (id: number, sectionIds: number[]) => apiV2.post(`/lesson-plans/${id}/class-availability/`, { section_ids: sectionIds }),
  copyToHook: (id: number, targetHookId: number, title?: string) =>
    apiV2.post(`/lesson-plans/${id}/copy-to-hook/`, {
      target_hook_id: targetHookId,
      title: title || undefined,
    }),
  resourceOperations: (id: number, sectionId: number) => apiV2.get(`/lesson-plans/${id}/resource-operations/`, {
    params: { section_id: sectionId },
  }),
  updateResourceOperation: (id: number, sectionId: number, data: any) => apiV2.patch(
    `/lesson-plans/${id}/resource-operations/?section_id=${sectionId}`,
    { ...data, section_id: sectionId },
  ),
  revalidateResource: (id: number, sectionId: number, resourceKey: string) => apiV2.post(
    `/lesson-plans/${id}/resource-operations/revalidate/`,
    { section_id: sectionId, resource_key: resourceKey },
  ),
  discoverDriveMaterials: (id: number) => apiV2.post(`/lesson-plans/${id}/drive-materials/discover/`, {}),
  steps: (id: number, params?: { audience?: 'student_preview' }) => apiV2.get(`/lesson-plans/${id}/steps/`, { params }),
  simulationTemplates: (id: number) => apiV2.get(`/lesson-plans/${id}/simulation-templates/`),
  addStep: (id: number, data: any) => apiV2.post(`/lesson-plans/${id}/steps/`, data),
  updateStep: (stepId: number, data: any) => apiV2.put(`/steps/${stepId}/`, data),
  deleteStep: (stepId: number) => apiV2.delete(`/steps/${stepId}/`),
  reorderSteps: (id: number, stepIds: number[]) => apiV2.post(`/lesson-plans/${id}/reorder-steps/`, { step_ids: stepIds }),
  repositoryCatalog: (hookId: number, projectKey?: string) => apiV2.get('/lesson-plans/repository-catalog/', {
    params: { hook_id: hookId, project_key: projectKey || undefined },
  }),
  importRepositoryLesson: (hookId: number, projectKey: string, sourceKey: string) => apiV2.post('/lesson-plans/import-bundle/', {
    hook_id: hookId,
    catalog_project_key: projectKey,
    catalog_source_key: sourceKey,
  }),
  updateRepositoryLesson: (hookId: number, projectKey: string, sourceKey: string, expectedSourceHash: string) => apiV2.post('/lesson-plans/import-bundle/', {
    hook_id: hookId,
    catalog_project_key: projectKey,
    catalog_source_key: sourceKey,
    catalog_update: true,
    expected_source_hash: expectedSourceHash,
  }),
  syncCatalogMaterials: (id: number, expectedSourceHash: string) => apiV2.post(`/lesson-plans/${id}/sync-catalog-materials/`, {
    expected_source_hash: expectedSourceHash,
  }),
  importBundle: (arg: { file?: File | null; bundle?: any; targetPlanId?: number; importMode?: 'append' | 'update' }) => {
    if (arg.file) {
      const form = new FormData();
      form.append('file', arg.file);
      if (arg.targetPlanId) {
        form.append('target_plan_id', String(arg.targetPlanId));
      }
      if (arg.importMode) {
        form.append('import_mode', arg.importMode);
      }
      return apiV2.post('/lesson-plans/import-bundle/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    const payload = { ...(arg.bundle ?? {}) } as Record<string, any>;
    if (arg.targetPlanId) {
      payload.target_plan_id = arg.targetPlanId;
    }
    if (arg.importMode) {
      payload.import_mode = arg.importMode;
    }
    return apiV2.post('/lesson-plans/import-bundle/', payload);
  },
};

export const lessonPreparationApi = {
  list: (sectionId?: number) => apiV2.get('/lesson-preparations/', {
    params: { section_id: sectionId || undefined },
  }).then((res) => res.data),
  analyze: (source: { run_id?: number; assignment_id?: number }) => apiV2.post('/lesson-preparations/', source).then((res) => res.data),
  get: (id: number) => apiV2.get(`/lesson-preparations/${id}/`).then((res) => res.data),
  saveReflection: (id: number, reflection: Record<string, any>, dismissedFindingIds: string[]) =>
    apiV2.patch(`/lesson-preparations/${id}/reflection/`, {
      reflection,
      dismissed_finding_ids: dismissedFindingIds,
    }).then((res) => res.data),
  refresh: (id: number) => apiV2.post(`/lesson-preparations/${id}/refresh/`, {}).then((res) => res.data),
  planningBrief: (id: number, data: {
    duration_minutes: number;
    next_meeting_date?: string;
    included_preparation_ids?: number[];
  }) => apiV2.post(`/lesson-preparations/${id}/planning-brief/`, data).then((res) => res.data),
  generate: (id: number, data: {
    duration_minutes: number;
    next_meeting_date?: string;
    provisional?: boolean;
    create_new_version?: boolean;
  }) => apiV2.post(`/lesson-preparations/${id}/generate/`, data).then((res) => res.data),
  approve: (id: number) => apiV2.post(`/lesson-preparations/${id}/approve/`, {}).then((res) => res.data),
};

export const lessonStepsApi = {
  aiDrafts: (stepId: number, params?: { surface?: 'class_draft' | 'teacher_walkthrough' }) =>
    apiV2.get(`/lesson-steps/${stepId}/ai-drafts/`, { params }).then((res) => res.data),
  createAiDraft: (
    stepId: number,
    payload: {
      surface: 'class_draft' | 'teacher_walkthrough';
      title?: string;
      content_md: string;
      structured_payload?: any;
      is_default?: boolean;
    },
  ) => apiV2.post(`/lesson-steps/${stepId}/ai-drafts/`, payload).then((res) => res.data),
  updateAiDraft: (
    stepId: number,
    draftId: number,
    payload: {
      title?: string;
      content_md?: string;
      structured_payload?: any;
      is_default?: boolean;
    },
  ) => apiV2.patch(`/lesson-steps/${stepId}/ai-drafts/${draftId}/`, payload).then((res) => res.data),
  deleteAiDraft: (stepId: number, draftId: number) =>
    apiV2.delete(`/lesson-steps/${stepId}/ai-drafts/${draftId}/`).then((res) => res.data),
};

export const lessonRunsApi = {
  list: () => apiV2.get('/lesson-runs/'),
  start: (data: any) => apiV2.post('/lesson-runs/', data),
  get: (id: number) => apiV2.get(`/lesson-runs/${id}/`),
  myActive: () => apiV2.get('/lesson-runs/my-active/').then((res) => res.data),
  active: (sectionId: number) => apiV2.get('/lesson-runs/active/', { params: { section: sectionId } }).then(res => res.data),
  end: (
    id: number,
    payload?: {
      attendance?: Array<{
        student_id: number;
        status: 'present' | 'absent';
        assign_makeup: boolean;
      }>;
      create_makeup?: boolean;
      due_at?: string | null;
      delivery_mode?: 'evidence_only' | 'replay_exam' | 'replay_lesson';
    },
  ) => apiV2.post(`/lesson-runs/${id}/end/`, payload ?? {}),
  updateAttendance: (
    id: number,
    payload: { student_id: number; status: 'present' | 'absent' },
  ) => apiV2.post(`/lesson-runs/${id}/attendance/`, payload).then((res) => res.data),
  redo: (id: number, data: { student_id: number; step_id: number }) => apiV2.post(`/lesson-runs/${id}/redo/`, data),
  reflectionGateDecision: (
    id: number,
    gateId: number,
    payload: { decision: 'allow' | 'revise'; note?: string },
  ) => apiV2.post(`/lesson-runs/${id}/reflection-gates/${gateId}/decision/`, payload).then((res) => res.data),
  submissionQualityQueue: (
    id: number,
    params?: { student_id?: number; step_id?: number; statuses?: string },
  ) => apiV2.get(`/lesson-runs/${id}/quality-queue/`, { params }).then((res) => res.data),
  submissionQualityDecision: (
    id: number,
    checkId: number,
    payload: { decision: 'clear' | 'assign_makeup' | 'group_reteach'; note?: string },
  ) => apiV2.post(`/lesson-runs/${id}/quality-checks/${checkId}/decision/`, payload).then((res) => res.data),
  control: (id: number) => apiV2.get(`/lesson-runs/${id}/control/`),
  resourceOpen: (id: number, resource: { title: string; src: string }) => apiV2.post(`/lesson-runs/${id}/resource-open/`, resource),
  enterStudio: (id: number) => apiV2.post(`/lesson-runs/${id}/enter-studio/`).then((res) => res.data),
  studioBoard: (id: number) => apiV2.get(`/lesson-runs/${id}/studio-board/`).then((res) => res.data),
  readinessGate: (id: number, stepId?: number) =>
    apiV2.get(`/lesson-runs/${id}/readiness-gate/`, { params: stepId ? { step_id: stepId } : undefined }).then((res) => res.data),
  routeReadinessGate: (
    id: number,
    payload: {
      student_ids: number[];
      route: 'go_test' | 'one_more_fix' | 'build_with_me';
      target_step_id?: number;
      expected_class_step_id?: number;
      expected_gate_step_id?: number;
    },
  ) => apiV2.post(`/lesson-runs/${id}/readiness-gate/route/`, payload).then((res) => res.data),
  routeStudentsToStep: (id: number, payload: RouteStudentsToStepPayload) =>
    apiV2.post(`/lesson-runs/${id}/route-students/`, payload).then((res) => res.data),
  advance: (id: number, step: number | { step_id: number }) => {
    const payload = typeof step === 'number' ? { step_id: step } : step;
    return apiV2.post(`/lesson-runs/${id}/advance/`, payload);
  },
  pacingTimer: (
    id: number,
    payload: { action: 'start' | 'restart' | 'add' | 'end'; seconds?: number },
  ) => apiV2.post(`/lesson-runs/${id}/pacing-timer/`, payload).then((res) => res.data),
  myState: (id: number) => apiV2.get(`/lesson-runs/${id}/my-state/`).then((res) => res.data),
  submitSimulationAction: (
    id: number,
    payload: {
      step_id: number;
      action_id: string;
      expected_revision: number;
      client_action_id: string;
    },
  ) => apiV2.post(`/lesson-runs/${id}/simulation/actions/`, payload).then((res) => res.data),
  udlSuggestions: (id: number) => apiV2.get(`/lesson-runs/${id}/udl-suggestions/`).then((res) => res.data),
  udlAssist: (
    id: number,
    payload: {
      step_id: number;
      assist_mode: 'simplify' | 'example' | 'hint' | 'step_breakdown';
    },
  ) => apiV2.post(`/lesson-runs/${id}/udl-assist/`, payload).then((res) => res.data),
  udlFollowup: (
    id: number,
    payload: {
      step_id: number;
      question: string;
      assist_mode?: 'simplify' | 'example' | 'hint' | 'step_breakdown';
      prior_help_title?: string;
      prior_help_content?: string;
    },
  ) => apiV2.post(`/lesson-runs/${id}/udl-followup/`, payload).then((res) => res.data),
  getStudioDraft: (id: number, stepId: number, expectedOwnerId?: number) =>
    apiV2.get(`/lesson-runs/${id}/studio-steps/${stepId}/draft/${expectedOwnerId ? `?expected_owner_id=${expectedOwnerId}` : ''}`).then((res) => res.data),
  saveStudioDraft: (id: number, stepId: number, draftPayload: Record<string, any>, checkpoint?: { expected_revision: number; request_id: string; context: string; expected_owner_id?: number }) =>
    apiV2.post(`/lesson-runs/${id}/studio-steps/${stepId}/draft/`, { draft_payload: draftPayload, ...checkpoint }).then((res) => res.data),
  requestStudioHelp: (
    id: number,
    stepId: number,
    payload: { help_kind?: string; help_note?: string; resolve?: boolean },
  ) => apiV2.post(`/lesson-runs/${id}/studio-steps/${stepId}/request-help/`, payload).then((res) => res.data),
  navigateStudio: (id: number, stepId: number) =>
    apiV2.post(`/lesson-runs/${id}/studio-navigate/`, { step_id: stepId }).then((res) => res.data),
  previousAnswers: (id: number, stepId: number) =>
    apiV2.get(`/lesson-runs/${id}/previous-answers/`, { params: { step_id: stepId } }).then((res) => res.data),
  review: (id: number, studentId?: number) =>
    apiV2.get(
      `/lesson-runs/${id}/review/`,
      { params: typeof studentId === 'number' ? { student_id: studentId } : undefined },
    ).then((res) => res.data),
  reviewPolicy: (
    id: number,
    payload: {
      review_access?: 'off' | 'after_end' | 'always';
      allow_catchup?: boolean;
      show_assessment_answers_in_review?: boolean;
    },
  ) => apiV2.post(`/lesson-runs/${id}/review-policy/`, payload).then((res) => res.data),
  markPresence: (id: number, event: 'enter' | 'heartbeat') =>
    apiV2.post(`/lesson-runs/${id}/presence/`, { event }).then((res) => res.data),
  presentationState: (id: number) =>
    apiV2.get(`/lesson-runs/${id}/my-state/`, { params: { presentation_only: 1 } }).then((res) => res.data),
  controlSummary: (id: number, stepId?: number) =>
    apiV2.get(`/lesson-runs/${id}/control-summary/`, { params: stepId ? { step_id: stepId } : undefined }),
  submissions: (id: number, stepId?: number) =>
    apiV2.get(`/lesson-runs/${id}/submissions/`, { params: stepId ? { step_id: stepId } : undefined }).then((res) => res.data),
  signal: (id: number, stepId: number, data: any) => apiV2.post(`/lesson-runs/${id}/signals/`, { step_id: stepId, ...data }),
  submitSignal: (id: number, stepId: number, data: any) => apiV2.post(`/lesson-runs/${id}/signals/`, { step_id: stepId, ...data }),
  setBroadcast: (id: number, data: { kind?: 'explain'; step_id?: number; content_md: string }) =>
    apiV2.post(`/lesson-runs/${id}/broadcast/`, data),
  clearBroadcast: (id: number) => apiV2.post(`/lesson-runs/${id}/broadcast/clear/`),
  setPresentation: (id: number, data: { active: boolean; target?: 'display' | 'students' | 'all' | 'sync'; resource_key?: string; src?: string; title?: string; slide_index?: number }) =>
    apiV2.post(`/lesson-runs/${id}/presentation/`, data),
  aiMicroAssist: (id: number, data: { prompt?: string; action_type?: string; context?: any }) =>
    apiV2.post(`/lesson-runs/${id}/ai-micro-assist/`, data),
  teacherAiGenerate: (
    id: number,
    data: {
      surface: 'class_draft' | 'class_draft_chat' | 'teacher_walkthrough' | 'teacher_chat';
      step_id?: number;
      draft_mode?: 'quick_explain' | 'step_by_step' | 'hint_only';
      walkthrough_mode?: 'teacher_deep' | 'student_friendly';
      walkthrough_adjustment?: 'standard' | 'shorter' | 'more_scaffolding';
      question?: string;
      parent_event_id?: number;
      include_lesson_context?: boolean;
    },
  ) => apiV2.post(`/lesson-runs/${id}/teacher-ai/`, data),
  teacherAiThread: (
    id: number,
    params: {
      surface: 'class_draft_chat' | 'teacher_chat';
      step_id?: number;
    },
  ) => apiV2.get(`/lesson-runs/${id}/teacher-ai/thread/`, { params }).then((res) => res.data),
  aiAssistFeedback: (
    id: number,
    assistId: number,
    payload: { usefulness_vote?: 'up' | 'down' | null; used_in_class?: boolean | null },
  ) => apiV2.post(`/lesson-runs/${id}/ai-assists/${assistId}/feedback/`, payload).then((res) => res.data),
  aiAssistFeedbackSummary: (id: number) =>
    apiV2.get(`/lesson-runs/${id}/ai-assists/feedback-summary/`).then((res) => res.data),
  overlayReply: (
    id: number,
    data: {
      step_id: number;
      text: string;
      overlay_mode?: 'repair' | 'extend' | null;
      preset_key?: string;
      action_type?: string;
    },
  ) => apiV2.post(`/lesson-runs/${id}/overlay-reply/`, data),
  getLatestEvidence: (id: number, stepId: number) =>
    apiV2.get(`/lesson-runs/${id}/evidence/latest/`, { params: { step_id: stepId } }).then((res) => res.data),
  submitEvidence: (runId: number, payload: { lesson_step_id: number; evidence_type: 'text' | 'file'; content: any }) =>
    apiV2.post(`/lesson-runs/${runId}/evidence/`, payload).then((res) => res.data),
  artifactStatusMy: (runId: number, stepId?: number) =>
    apiV2.get(`/lesson-runs/${runId}/artifact-status/my/`, { params: stepId ? { step_id: stepId } : undefined }).then((res) => res.data),
  artifactMarkDrafting: (runId: number, stepId: number, payload?: { metadata?: Record<string, any> }) =>
    apiV2.post(`/lesson-runs/${runId}/artifact-status/steps/${stepId}/mark-drafting/`, payload ?? {}).then((res) => res.data),
  artifactCoach: (
    runId: number,
    stepId: number,
    payload: { draft_text?: string; draft_link?: string; draft_note?: string },
  ) => apiV2.post(`/lesson-runs/${runId}/artifact-status/steps/${stepId}/coach/`, payload).then((res) => res.data),
  artifactMarkSubmitted: (
    runId: number,
    stepId: number,
    payload?: { source_url?: string; source_ref?: string; metadata?: Record<string, any> },
  ) => apiV2.post(`/lesson-runs/${runId}/artifact-status/steps/${stepId}/mark-submitted/`, payload ?? {}).then((res) => res.data),
  pacing: (id: number, pacingMode: string) => apiV2.post(`/lesson-runs/${id}/pacing/`, { pacing_mode: pacingMode }),
  setTestMode: (id: number, isTestMode: boolean) =>
    apiV2.post(`/lesson-runs/${id}/test-mode/`, { is_test_mode: isTestMode }),
  audience: (id: number, studentIds: number[], mode: 'join' | 'leave') => apiV2.post(`/lesson-runs/${id}/audience/`, { student_ids: studentIds, mode }),
  trackAssignment: (
    id: number,
    payload: {
      track: boolean;
      due_at?: string | null;
      delivery_mode?: 'evidence_only' | 'replay_exam' | 'replay_lesson';
      target_scope?: 'all' | 'absent' | 'present_no_work' | 'participated' | 'incomplete' | 'absent_or_incomplete' | 'needs_makeup' | 'custom';
      target_student_ids?: number[];
    }
  ) =>
    apiV2.post(`/lesson-runs/${id}/track-assignment/`, payload).then((res) => res.data),
  getTrackAssignment: (id: number) => apiV2.get(`/lesson-runs/${id}/track-assignment/`).then((res) => res.data),
  reveal: (id: number, payload: { step_id: number }) => apiV2.post(`/lesson-runs/${id}/reveal/`, payload),

  // Adaptive runtime (Phase 1)
  adaptiveState: (id: number) => apiV2.get(`/lesson-runs/${id}/adaptive-state/`).then((res) => res.data),
  adaptiveSuggestions: (id: number, status?: string) =>
    apiV2.get(`/lesson-runs/${id}/adaptive-suggestions/`, { params: status ? { status } : undefined }).then((res) => res.data),
  generateAdaptiveSuggestions: (id: number) =>
    apiV2.post(`/lesson-runs/${id}/adaptive-suggestions/generate/`).then((res) => res.data),
  approveAdaptiveSuggestion: (id: number, suggestionId: number, payload?: { apply?: boolean }) =>
    apiV2.post(`/lesson-runs/${id}/adaptive-suggestions/${suggestionId}/approve/`, payload ?? {}).then((res) => res.data),
  applyAdaptiveSuggestion: (id: number, suggestionId: number, payload?: { allow_stale?: boolean; overrides?: Record<string, any> }) =>
    apiV2.post(`/lesson-runs/${id}/adaptive-suggestions/${suggestionId}/apply/`, payload ?? {}).then((res) => res.data),
  rejectAdaptiveSuggestion: (id: number, suggestionId: number, payload?: { decision_note?: string }) =>
    apiV2.post(`/lesson-runs/${id}/adaptive-suggestions/${suggestionId}/reject/`, payload ?? {}).then((res) => res.data),
  clearStudentOverrides: (
    id: number,
    payload:
      | { return_to: 'class_step' | 'origin_step'; student_ids?: number[] }
      | { target_step_id: number; student_ids?: number[] }
  ) => apiV2.post(`/lesson-runs/${id}/student-overrides/clear/`, payload).then((res) => res.data),
};

export const studioProgressApi = {
  acknowledgeHelp: (progressId: number) =>
    apiV2.post(`/studio-progress/${progressId}/help/acknowledge/`).then((res) => res.data),
  resolveHelp: (progressId: number) =>
    apiV2.post(`/studio-progress/${progressId}/help/resolve/`).then((res) => res.data),
};

export const evidenceApi = {
  list: (hookId: number) => apiV2.get('/evidence/', { params: { hook: hookId } }),
  submit: (data: any) => apiV2.post('/evidence/', data),
  revise: (id: number, content: any) => apiV2.post(`/evidence/${id}/revise/`, { content }),
  approve: (id: number) => apiV2.post(`/evidence/${id}/approve/`),
  reviewQueue: (params?: { hook?: number; stage?: string; status?: string; run_id?: number; step_id?: number }) =>
    apiV2.get('/evidence/review-queue/', { params }),
  submitFeedback: (id: number, data: any) => apiV2.post(`/evidence/${id}/feedback/`, data),
};

export const artifactReviewApi = {
  reviewQueue: (params?: {
    statuses?: string;
    status?: string;
    section_id?: number;
    hook_id?: number;
    step_id?: number;
    student_id?: number;
  }) => apiV2.get('/artifact-status/review-queue/', { params }).then((res) => res.data),
  markReviewing: (statusId: number, payload?: { teacher_note?: string }) =>
    apiV2.post(`/artifact-status/${statusId}/mark-reviewing/`, payload ?? {}).then((res) => res.data),
  approve: (statusId: number, payload?: { teacher_note?: string }) =>
    apiV2.post(`/artifact-status/${statusId}/approve/`, payload ?? {}).then((res) => res.data),
  requestRevision: (statusId: number, payload?: { teacher_note?: string }) =>
    apiV2.post(`/artifact-status/${statusId}/request-revision/`, payload ?? {}).then((res) => res.data),
};

export const assignmentsApi = {
  my: () => apiV2.get('/assignments/my/').then(res => res.data),
  teacherQueue: (
    sectionVisibility: 'current' | 'archived' | 'all' = 'current',
    purpose: 'makeup' | 'assessment' | 'practice' = 'makeup',
  ) =>
    apiV2.get('/assignments/teacher-queue/', {
      params: { section_visibility: sectionVisibility, purpose },
    }).then(res => res.data),
  teacherDetail: (assignmentId: number, studentId: number) =>
    apiV2.get(`/assignments/${assignmentId}/students/${studentId}/`).then((res) => res.data),
  classSummary: (assignmentId: number, threshold = 70, assignmentIds?: number[]) =>
    apiV2.get(`/assignments/${assignmentId}/class-summary/`, {
      params: { threshold, assignment_ids: assignmentIds?.join(',') || undefined },
    }).then((res) => res.data),
  assessmentActionPlan: (assignmentId: number, threshold = 70, assignmentIds?: number[]) =>
    apiV2.get(`/assignments/${assignmentId}/class-summary/action-plan/`, {
      params: { threshold, assignment_ids: assignmentIds?.join(',') || undefined },
    }).then((res) => res.data),
  draftClassSummary: (assignmentId: number, threshold = 70, assignmentIds?: number[]) =>
    apiV2.post(`/assignments/${assignmentId}/class-summary/ai-draft/`, {
      threshold,
      assignment_ids: assignmentIds || undefined,
    }).then((res) => res.data),
  udlSuggestions: (assignmentId: number, stepId?: number) =>
    apiV2.get(
      `/assignments/${assignmentId}/udl-suggestions/`,
      { params: typeof stepId === 'number' ? { step_id: stepId } : undefined },
    ).then((res) => res.data),
  udlAssist: (
    assignmentId: number,
    payload: {
      step_id: number;
      assist_mode: 'simplify' | 'example' | 'hint' | 'step_breakdown';
    },
  ) => apiV2.post(`/assignments/${assignmentId}/udl-assist/`, payload).then((res) => res.data),
  udlFollowup: (
    assignmentId: number,
    payload: {
      step_id: number;
      question: string;
      assist_mode?: 'simplify' | 'example' | 'hint' | 'step_breakdown';
      prior_help_title?: string;
      prior_help_content?: string;
    },
  ) => apiV2.post(`/assignments/${assignmentId}/udl-followup/`, payload).then((res) => res.data),
  previousAnswers: (assignmentId: number, stepId: number) =>
    apiV2.get(`/assignments/${assignmentId}/previous-answers/`, { params: { step_id: stepId } }).then((res) => res.data),
  create: (payload: {
    section_id: number;
    lesson_plan_id: number;
    due_at?: string | null;
    is_active?: boolean;
    delivery_mode?: 'evidence_only' | 'replay_exam' | 'replay_lesson';
    purpose?: 'makeup' | 'assessment' | 'practice';
    assessment_stage?: 'define' | 'do' | 'explain' | 'apply';
    standards_snapshot?: Array<string | Record<string, unknown>>;
    external_next_url?: string;
    target_scope?: 'all' | 'custom' | 'group';
    target_student_ids?: number[];
    target_group_name?: string;
    retake_policy?: 'automatic' | 'teacher_approval' | 'disabled';
    retake_remediation_instructions?: string;
  }) => apiV2.post('/assignments/', payload).then(res => res.data),
  detail: (assignmentId: number) => apiV2.get(`/assignments/${assignmentId}/`).then(res => res.data),
  start: (assignmentId: number) => apiV2.post(`/assignments/${assignmentId}/start/`).then(res => res.data),
  aiAssist: (
    assignmentId: number,
    payload: {
      step_id: number;
      action_type: 'missed_answer_explanation' | 'missed_answer_more_detail' | 'missed_answer_followup';
      question?: string;
      parent_event_id?: number;
    },
  ) => apiV2.post(`/assignments/${assignmentId}/ai-assist/`, payload).then((res) => res.data),
  excuseStudent: (
    assignmentId: number,
    studentId: number,
    payload: { excused: boolean; reason?: string },
  ) => apiV2.post(`/assignments/${assignmentId}/students/${studentId}/excuse/`, payload).then(res => res.data),
  reviewMakeup: (assignmentId: number, studentId: number, payload: { decision: 'approved' | 'needs_revision'; note?: string }) =>
    apiV2.post(`/assignments/${assignmentId}/students/${studentId}/makeup-review/`, payload).then((res) => res.data),
  submitResponse: (assignmentId: number, payload: { step_id: number; response: Record<string, any> }) =>
    apiV2.post(`/assignments/${assignmentId}/responses/`, payload).then(res => res.data),
  submitExam: (assignmentId: number) => apiV2.post(`/assignments/${assignmentId}/submit/`).then(res => res.data),
  retakeQuiz: (assignmentId: number) => apiV2.post(`/assignments/${assignmentId}/retake/`).then(res => res.data),
  requestRetake: (assignmentId: number, payload: { remediation_response?: string }) =>
    apiV2.post(`/assignments/${assignmentId}/retake-request/`, payload).then(res => res.data),
  decideRetake: (assignmentId: number, studentId: number, payload: { decision: 'approved' | 'denied'; teacher_note?: string; required_remediation?: string[] }) =>
    apiV2.post(`/assignments/${assignmentId}/students/${studentId}/retake-decision/`, payload).then(res => res.data),
  requireRetake: (assignmentId: number, studentId: number, payload: { reason: string }) =>
    apiV2.post(`/assignments/${assignmentId}/students/${studentId}/require-retake/`, payload).then(res => res.data),
  confirmExternalSubmission: (assignmentId: number) =>
    apiV2.post(`/assignments/${assignmentId}/confirm-external-submission/`, {}).then((res) => res.data),
  reviewResponse: (responseId: number, payload: { status: 'reviewed' | 'needs_revision'; notes?: string; criteria_notes?: Record<string, unknown> }) =>
    apiV2.post(`/assignments/responses/${responseId}/review/`, payload).then((res) => res.data),
  batchReviewResponses: (assignmentId: number, studentId: number, payload: {
    teacher_attestation: boolean;
    reviews: Array<{
      response_id: number;
      status: 'reviewed' | 'needs_revision';
      evidence_quality_rating: 'exceeds' | 'meets' | 'approaching' | 'beginning';
      notes?: string;
    }>;
  }) => apiV2.post(`/assignments/${assignmentId}/students/${studentId}/responses/batch-review/`, payload).then((res) => res.data),
  analyzeWrittenResponse: (responseId: number) =>
    apiV2.post(`/assignments/responses/${responseId}/ai-review/`, {}).then((res) => res.data),
  draftVerification: (assignmentId: number, studentId: number) =>
    apiV2.post(`/assignments/${assignmentId}/students/${studentId}/ai-verification-draft/`, {}).then((res) => res.data),
  addObservation: (assignmentId: number, studentId: number, payload: {
    kind: 'hands_on' | 'verbal';
    outcome: 'verified' | 'not_yet' | 'not_observed';
    skill_label: string;
    notes?: string;
    lesson_step_id?: number;
  }) => apiV2.post(`/assignments/${assignmentId}/students/${studentId}/observations/`, payload).then((res) => res.data),
  verify: (assignmentId: number, studentId: number, payload: { teacher_feedback?: string; evidence_summary?: string }) =>
    apiV2.put(`/assignments/${assignmentId}/students/${studentId}/verification/`, payload).then((res) => res.data),
  uploadAttachment: (assignmentId: number, file: File, lessonStepId?: number) => {
    const form = new FormData();
    form.append('file', file);
    if (lessonStepId) form.append('lesson_step_id', String(lessonStepId));
    return apiV2.post(`/assignments/${assignmentId}/attachments/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data);
  },
};

export const assessmentAuthoringApi = {
  list: () => apiV2.get('/assessment-authoring/').then((res) => res.data),
  get: (id: number) => apiV2.get(`/assessment-authoring/${id}/`).then((res) => res.data),
  create: (schema: Record<string, any>) => apiV2.post('/assessment-authoring/', { schema }).then((res) => res.data),
  update: (id: number, schema: Record<string, any>) => apiV2.put(`/assessment-authoring/${id}/`, { schema }).then((res) => res.data),
  importPreview: (text: string) => apiV2.post('/assessment-authoring/import-preview/', { text }).then((res) => res.data),
  repositoryCatalog: (projectKey?: string) => apiV2.get('/assessment-authoring/repository-catalog/', {
    params: { project_key: projectKey || undefined },
  }).then((res) => res.data),
  repositoryPreview: (sourceKey: string, projectKey: string, expectedSourceHash: string) => apiV2.post(
    '/assessment-authoring/repository-preview/',
    {
      source_key: sourceKey,
      project_key: projectKey,
      expected_source_hash: expectedSourceHash,
    },
  ).then((res) => res.data),
  publish: (id: number) => apiV2.post(`/assessment-authoring/${id}/publish/`, {}).then((res) => res.data),
};

export const diagnosticsApi = {
  list: (sectionId: number) => apiV2.get(`/diagnostics/sections/${sectionId}/`).then((res) => res.data),
  detail: (sectionId: number, assessmentId: number) =>
    apiV2.get(`/diagnostics/sections/${sectionId}/assessments/${assessmentId}/`).then((res) => res.data),
  preview: (sectionId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiV2.post(`/diagnostics/sections/${sectionId}/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data);
  },
  import: (sectionId: number, file: File, payload: {
    title: string;
    mastery_threshold: number;
    mappings: Record<string, number>;
  }) => {
    const form = new FormData();
    form.append('file', file);
    form.append('commit', 'true');
    form.append('title', payload.title);
    form.append('mastery_threshold', String(payload.mastery_threshold));
    form.append('mappings', JSON.stringify(payload.mappings));
    return apiV2.post(`/diagnostics/sections/${sectionId}/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data);
  },
  updateStandard: (sectionId: number, standardResultId: number, payload: {
    support_selected?: boolean;
    not_taught_yet?: boolean;
  }) => apiV2.patch(`/diagnostics/sections/${sectionId}/standards/${standardResultId}/`, payload).then((res) => res.data),
  my: (sectionId?: number) => apiV2.get('/diagnostics/my/', {
    params: sectionId ? { section_id: sectionId } : undefined,
  }).then((res) => res.data),
};

export const exportApi = {
  generate: (data: any) => apiV2.post('/export/headrush/', data),
  generateGoalEvidence: (goalId: number) =>
    apiV2.post(`/export/goals/${goalId}/technical-evidence/`, {}).then((res) => res.data),
  regenerateGoalEvidence: (exportId: number) =>
    apiV2.post(`/export/${exportId}/regenerate/`, {}).then((res) => res.data),
  download: (id: number) => apiV2.get(`/export/${id}/download/`, { responseType: 'blob' }),
  generateAssessmentEvidence: (assignmentId: number, studentId?: number) =>
    apiV2.post(`/export/assessments/${assignmentId}/evidence/`, studentId ? { student_id: studentId } : {}).then((res) => res.data),
};

export const reportsApi = {
  myWorkReview: (params?: { from?: string; to?: string }) =>
    apiV2.get<StudentReview>('/reports/my/work-review/', { params }).then((res) => res.data),
  downloadMyWorkReview: (data: { from: string; to: string; item_ids: string[] }) =>
    apiV2.post('/reports/my/work-review/', data, { responseType: 'blob' }),
  refreshRun: (runId: number) => apiV2.post(`/reports/runs/${runId}/refresh/`),
  runStudents: (
    runId: number,
    params?: { refresh?: boolean; include_steps?: boolean },
  ) => apiV2.get(`/reports/runs/${runId}/students/`, { params }),
  refreshStudent: (
    studentId: number,
    params?: { from?: string; to?: string },
  ) => apiV2.post(`/reports/students/${studentId}/refresh/`, undefined, { params }),
  studentRuns: (
    studentId: number,
    params?: {
      section_id?: number;
      hook_id?: number;
      from?: string;
      to?: string;
      refresh?: boolean;
      include_steps?: boolean;
    },
  ) => apiV2.get(`/reports/students/${studentId}/runs/`, { params }),
  studentDaily: (
    studentId: number,
    params?: {
      section_id?: number;
      hook_id?: number;
      from?: string;
      to?: string;
      refresh?: boolean;
    },
  ) => apiV2.get(`/reports/students/${studentId}/daily/`, { params }),
  studentWorkHistory: (
    studentId: number,
    params?: {
      section_id?: number;
      grade_level?: string;
      from?: string;
      to?: string;
      q?: string;
      type?: string;
      status?: string;
      refresh?: boolean;
    },
  ) => apiV2.get(`/reports/students/${studentId}/work-history/`, { params }).then((res) => res.data),
  sectionCompletion: (
    sectionId: number,
    params?: {
      from?: string;
      to?: string;
      grain?: 'day' | 'week';
      include_live?: boolean;
      include_makeup?: boolean;
      include_external?: boolean;
      include_items?: boolean;
    },
  ) => apiV2.get(`/reports/sections/${sectionId}/completion/`, { params }),
};

export const dailyCompletionApi = {
  overview: (params: { section_id: string; period: string; cycle_key?: string }) => apiV2.get('/class-overview/', { params }),
  list: (params?: {
    section_id?: number;
    start_date?: string;
    end_date?: string;
    completion_status?: string;
    needs_review?: boolean;
    student_id?: number;
  }) => apiV2.get('/daily-completion/', { params }),
  get: (id: number) => apiV2.get(`/daily-completion/${id}/`),
  matrix: (params: {
    section_id: number;
    start_date: string;
    end_date: string;
    show_incomplete_only?: boolean;
  }) => apiV2.get('/daily-completion/matrix/', { params }),
  regenerate: (data: { date: string; section_id?: number }) => apiV2.post('/daily-completion/regenerate/', data),
};

export const reportingCyclesApi = {
  list: () => apiV2.get('/reporting-cycles/'),
  save: (data: { title: string; start_date: string; end_date: string; section_ids: number[] }, id?: number) =>
    id ? apiV2.patch(`/reporting-cycles/${id}/`, data) : apiV2.post('/reporting-cycles/', data),
};

export const lessonCompletionApi = {
  get: (runId: number) => apiV2.get(`/lesson-runs/${runId}/completion-requirements/`),
  adjust: (runId: number, data: { step_id: number; decision: string; student_ids: number[]; reason: string }) =>
    apiV2.post(`/lesson-runs/${runId}/completion-requirements/`, data),
};

export const groupRecommendationsApi = {
  recommendations: (params: {
    section_id: number;
    target_date?: string;
  }) => apiV2.get('/group-recommendations/recommendations/', { params }),
};

export const actionListsApi = {
  dailyBrief: (params: {
    section_id: number;
    target_date?: string;
  }) => apiV2.get('/action-lists/daily-brief/', { params }),
};

export const studentActionQueueApi = {
  get: (sectionId: number, params?: { date?: string; include_snoozed?: boolean; include_closed?: boolean }) =>
    apiV2.get(`/action-queue/sections/${sectionId}/`, { params }),
  updateWorkflow: (
    sectionId: number,
    studentId: number,
    payload: { status: 'open' | 'in_progress' | 'snoozed' | 'resolved' | 'dismissed'; note?: string; snoozed_until?: string; date?: string },
  ) => apiV2.patch(`/action-queue/sections/${sectionId}/students/${studentId}/workflow/`, payload),
  contacts: (sectionId: number, studentId: number) =>
    apiV2.get(`/action-queue/sections/${sectionId}/students/${studentId}/contacts/`),
  logContact: (
    sectionId: number,
    studentId: number,
    payload: {
      method: 'email' | 'phone' | 'other';
      status: 'drafted' | 'attempted' | 'completed';
      subject?: string;
      message?: string;
      outcome?: string;
      follow_up_on?: string;
    },
  ) => apiV2.post(`/action-queue/sections/${sectionId}/students/${studentId}/contacts/`, payload),
  aiAssist: (
    sectionId: number,
    studentId: number,
    payload: { action_type: 'summarize' | 'suggest_intervention' | 'draft_family_email' | 'draft_phone_outline'; date?: string },
  ) => apiV2.post(`/action-queue/sections/${sectionId}/students/${studentId}/ai-assist/`, payload),
  aiFeedback: (eventId: number, accepted: boolean) =>
    apiV2.patch(`/action-queue/ai-assists/${eventId}/feedback/`, { accepted }),
  checkIns: (sectionId: number, studentId: number) =>
    apiV2.get(`/action-queue/sections/${sectionId}/students/${studentId}/check-ins/`),
  createCheckIn: (
    sectionId: number,
    studentId: number,
    payload: {
      instructional_cycle: number;
      milestone?: number | null;
      check_in_kind: 'midpoint' | 'instructional';
      signal_fingerprint?: string;
      request_token: string;
      barrier_problem?: string;
      instructional_move?: string;
      agreed_next_deliverable: string;
      follow_up_on?: string | null;
      teacher_requested?: boolean;
      as_of_date?: string;
    },
  ) => apiV2.post(`/action-queue/sections/${sectionId}/students/${studentId}/check-ins/`, payload),
  myCheckIns: (sectionId: number) =>
    apiV2.get('/action-queue/my/check-ins/', { params: { section_id: sectionId } }),
};

export const goalsApi = {
  instructionalCycles: {
    list: (sectionId: number) =>
      apiV2.get(`/goals/sections/${sectionId}/instructional-cycles/`).then((res) => res.data),
    create: (sectionId: number, payload: {
      parent?: number | null;
      title: string;
      cycle_kind: 'instructional_period' | 'performance_period' | 'transition_review';
      audience_grades?: string[];
      start_date: string;
      end_date: string;
      sequence?: number;
    }) => apiV2.post(`/goals/sections/${sectionId}/instructional-cycles/`, payload).then((res) => res.data),
    update: (sectionId: number, cycleId: number, payload: Record<string, unknown>) =>
      apiV2.patch(`/goals/sections/${sectionId}/instructional-cycles/${cycleId}/`, payload).then((res) => res.data),
    transition: (sectionId: number, cycleId: number, action: 'activate' | 'review' | 'finalize') =>
      apiV2.post(`/goals/sections/${sectionId}/instructional-cycles/${cycleId}/transition/`, { action }).then((res) => res.data),
    setGoalCycle: (sectionId: number, studentId: number, goalId: number, cycleId: number | null) =>
      apiV2.patch(`/goals/sections/${sectionId}/students/${studentId}/primary-goals/${goalId}/cycle/`, {
        instructional_cycle: cycleId,
      }).then((res) => res.data),
  },
  myToday: (params?: { section_id?: number }) => apiV2.get('/goals/my/today/', { params }).then((res) => res.data),
  myHistory: (params?: { section_id?: number; limit?: number }) =>
    apiV2.get('/goals/my/history/', { params }).then((res) => res.data),
  primaryGoal: {
    active: (params: { section_id: number; include_inactive?: boolean }) =>
      apiV2.get('/goals/my/primary-goal/', { params }).then((res) => res.data),
    create: (payload: {
      section_id: number;
      title: string;
      why_text: string;
      target_metric_text: string;
      goal_track?: 'required_pathway' | 'personal_project';
      goal_purpose: 'technical_cte' | 'personal_professional';
      start_date: string;
      end_date: string;
      instructional_cycle?: number | null;
      is_active?: boolean;
    }) => apiV2.post('/goals/my/primary-goal/', payload).then((res) => res.data),
    update: (
      goalId: number,
      payload: {
        title?: string;
        why_text?: string;
        target_metric_text?: string;
        goal_track?: 'required_pathway' | 'personal_project';
        goal_purpose?: 'technical_cte' | 'personal_professional';
        start_date?: string;
        end_date?: string;
        instructional_cycle?: number | null;
        is_active?: boolean;
      },
    ) => apiV2.patch(`/goals/my/primary-goals/${goalId}/`, payload).then((res) => res.data),
    convertToDirection: (goalId: number) =>
      apiV2.post(`/goals/my/primary-goals/${goalId}/convert-to-direction/`).then((res) => res.data),
    milestones: {
      list: (goalId: number) =>
        apiV2.get(`/goals/my/primary-goals/${goalId}/milestones/`).then((res) => res.data),
      create: (goalId: number, payload: {
        hook_stage?: '' | 'define' | 'do' | 'explain' | 'apply';
        expected_outcome: string;
        due_date: string;
        status?: 'planned' | 'in_progress' | 'blocked' | 'ready_for_review' | 'completed';
        progress_note?: string;
        evidence_expected?: boolean;
        evidence_description?: string;
        evidence_url?: string;
        headrush_status?: 'not_uploaded' | 'uploaded';
      }) => apiV2.post(`/goals/my/primary-goals/${goalId}/milestones/`, payload).then((res) => res.data),
      update: (goalId: number, milestoneId: number, payload: {
        hook_stage?: '' | 'define' | 'do' | 'explain' | 'apply';
        expected_outcome?: string;
        due_date?: string;
        status?: 'planned' | 'in_progress' | 'blocked' | 'ready_for_review' | 'completed';
        progress_note?: string;
        evidence_expected?: boolean;
        evidence_description?: string;
        evidence_url?: string;
        headrush_status?: 'not_uploaded' | 'uploaded';
      }) => apiV2.patch(`/goals/my/primary-goals/${goalId}/milestones/${milestoneId}/`, payload).then((res) => res.data),
    },
    evidenceLinks: {
      list: (goalId: number) =>
        apiV2.get(`/goals/my/primary-goals/${goalId}/evidence-links/`).then((res) => res.data),
      create: (goalId: number, payload: {
        source_type: 'evidence' | 'official_artifact' | 'assignment_response' | 'step_signal';
        source_id: number;
        milestone?: number | null;
        lesson_run?: number | null;
      }) => apiV2.post(`/goals/my/primary-goals/${goalId}/evidence-links/`, payload).then((res) => res.data),
      remove: (goalId: number, linkId: number) =>
        apiV2.delete(`/goals/my/primary-goals/${goalId}/evidence-links/${linkId}/`).then((res) => res.data),
    },
    aiReview: (goalId: number, revision?: number, digest?: string) =>
      apiV2.post(`/goals/my/primary-goals/${goalId}/ai-review/`, {
        definition_revision: revision,
        definition_digest: digest,
      }).then((res) => res.data),
    standardCandidates: (goalId: number) =>
      apiV2.get(`/goals/my/primary-goals/${goalId}/standard-candidates/`).then((res) => res.data),
    standardsCatalog: (params?: { pathway?: string; q?: string }) =>
      apiV2.get('/goals/standards/', { params }).then((res) => res.data),
    standardSuggestions: (goalId: number) =>
      apiV2.get(`/goals/my/primary-goals/${goalId}/practice-standard-suggestions/`).then((res) => res.data),
    practiceStandards: {
      add: (goalId: number, standardId: number) =>
        apiV2.post(`/goals/my/primary-goals/${goalId}/practice-standards/`, { standard_id: standardId }).then((res) => res.data),
      remove: (goalId: number, focusId: number) =>
        apiV2.delete(`/goals/my/primary-goals/${goalId}/practice-standards/${focusId}/`).then((res) => res.data),
    },
    finalReflection: {
      get: (goalId: number) =>
        apiV2.get(`/goals/my/primary-goals/${goalId}/final-reflection/`).then((res) => res.data),
      save: (
        goalId: number,
        payload: {
          outcome_status: 'met' | 'partially_met' | 'not_met';
          evidence_text: string;
          what_worked_text: string;
          obstacle_text?: string;
          next_time_change_text: string;
          nsr_focus_reflection_text?: string;
          next_goal_direction_text?: string;
        },
      ) => apiV2.post(`/goals/my/primary-goals/${goalId}/final-reflection/`, payload).then((res) => res.data),
      update: (
        goalId: number,
        payload: {
          outcome_status?: 'met' | 'partially_met' | 'not_met';
          evidence_text?: string;
          what_worked_text?: string;
          obstacle_text?: string;
          next_time_change_text?: string;
          nsr_focus_reflection_text?: string;
          next_goal_direction_text?: string;
        },
      ) => apiV2.patch(`/goals/my/primary-goals/${goalId}/final-reflection/`, payload).then((res) => res.data),
    },
  },
  longerTermDirection: {
    save: (payload: {
      section_id: number;
      title: string;
      why_text?: string;
      target_metric_text?: string;
    }) => apiV2.put('/goals/my/longer-term-direction/', payload).then((res) => res.data),
  },
  weeklyCheckpoint: {
    current: (params: { section_id: number; limit?: number }) =>
      apiV2.get('/goals/my/weekly-checkpoint/', { params }).then((res) => res.data),
    save: (payload: {
      section_id: number;
      progress_text: string;
      evidence_text?: string;
      obstacle_text?: string;
      adjustment_text?: string;
      confidence_rating: number;
      limit?: number;
    }) => apiV2.post('/goals/my/weekly-checkpoint/', payload).then((res) => res.data),
    update: (payload: {
      section_id: number;
      progress_text?: string;
      evidence_text?: string;
      obstacle_text?: string;
      adjustment_text?: string;
      confidence_rating?: number;
      limit?: number;
    }) => apiV2.patch('/goals/my/weekly-checkpoint/', payload).then((res) => res.data),
  },
  dailyNudge: (payload: { section_id: number }) =>
    apiV2.post('/goals/my/daily-nudge/', payload).then((res) => res.data),
  checkIn: (payload: {
    section_id: number;
    start_work?: boolean;
    current_focus?: {
      key: string;
      label: string;
      title?: string;
      source_type?: string;
      source_id?: number;
      origin?: 'inferred' | 'student' | 'runtime';
      planned_goal_ids?: number[];
      secondary_title?: string;
    };
    primary_goal_id?: number;
    connect_primary_goal?: boolean;
    learning_goal_text?: string;
    completion_goal_text?: string;
    daily_plan?: {
      track?: string;
      target?: string;
      first_step?: string;
      success_criteria?: string;
      work_item?: {
        kind: string;
        id?: number | null;
        title: string;
        source?: string;
        track_key?: string;
        project_role?: '' | 'main_project' | 'secondary_project';
      } | null;
    };
    plan_track?: string;
    plan_target?: string;
    plan_first_step?: string;
    plan_success_criteria?: string;
    plan_work_item?: Record<string, any> | null;
    project_plan_target?: string;
    project_plan_success_criteria?: string;
    protected_goal_minutes?: number;
    planned_secondary_work?: string;
    planned_work_mode?: string;
    learning_goal_builder?: {
      skill?: string;
      action?: string;
      proof?: string;
      first_step?: string;
    };
    first_step_text?: string;
    completion_goal_builder?: {
      mode?: 'work_item' | 'manual';
      project_role?: '' | 'main_project' | 'secondary_project';
      selected_work_item?: {
        kind: string;
        id?: number | null;
        title: string;
        source?: string;
        track_key?: string;
        project_role?: '' | 'main_project' | 'secondary_project';
      } | null;
      manual_text?: string;
    };
    learning_stage_tag?: string | null;
    learning_readiness_tags?: string[];
    completion_readiness_tags?: string[];
    cycle_readiness_tags?: string[];
    learning_ai_assisted?: boolean;
    learning_ai_rationale?: string;
  }) => apiV2.post('/goals/my/check-in/', payload).then((res) => res.data),
  updateGoal: (
    goalId: number,
    payload: {
      smart_text?: string;
      stage_tag?: string | null;
      status?: 'not_started' | 'in_progress' | 'completed';
      self_score?: number | null;
      readiness_tags?: string[];
      ai_assisted?: boolean;
      ai_rationale?: string;
    },
  ) => apiV2.patch(`/goals/my/goals/${goalId}/`, payload).then((res) => res.data),
  checkOut: (payload: {
    section_id: number;
    reflection_text?: string;
    goal_status?: 'yes' | 'partial' | 'no';
    priority_result?: 'met' | 'partially_met' | 'blocked' | 'changed_authentic_work' | 'ready_for_review';
    pathway_evidence_text?: string;
    pathway_evidence_url?: string;
    project_result?: 'met' | 'partially_met' | 'blocked' | 'changed_authentic_work' | 'ready_for_review';
    project_evidence_text?: string;
    project_evidence_url?: string;
    project_portfolio_candidate?: boolean;
    next_step_text?: string;
    plan_change_reason?: string;
    plan_change_note?: string;
    moved_toward_primary_goal?: boolean | null;
    completion_rating?: number;
    confidence_rating?: number;
    readiness_tags?: string[];
    goals?: Array<{ id: number; status?: 'not_started' | 'in_progress' | 'completed'; self_score?: number | null }>;
  }) => apiV2.post('/goals/my/check-out/', payload).then((res) => res.data),
  teacherMilestones: {
    list: (sectionId: number, studentId: number, goalId: number) =>
      apiV2.get(`/goals/sections/${sectionId}/students/${studentId}/primary-goals/${goalId}/milestones/`).then((res) => res.data),
    update: (sectionId: number, studentId: number, goalId: number, milestoneId: number, payload: {
      hook_stage?: '' | 'define' | 'do' | 'explain' | 'apply';
      expected_outcome?: string;
      due_date?: string;
      evidence_expected?: boolean;
      verification_status?: 'not_submitted' | 'pending' | 'verified' | 'needs_revision';
      verification_note?: string;
    }) => apiV2.patch(
      `/goals/sections/${sectionId}/students/${studentId}/primary-goals/${goalId}/milestones/${milestoneId}/`,
      payload,
    ).then((res) => res.data),
  },
  teacherEvidenceLinks: {
    list: (sectionId: number, studentId: number, goalId: number) =>
      apiV2.get(`/goals/sections/${sectionId}/students/${studentId}/primary-goals/${goalId}/evidence-links/`).then((res) => res.data),
    create: (sectionId: number, studentId: number, goalId: number, payload: {
      source_type: 'evidence' | 'official_artifact' | 'assignment_response' | 'step_signal';
      source_id: number;
      milestone?: number | null;
      lesson_run?: number | null;
    }) => apiV2.post(`/goals/sections/${sectionId}/students/${studentId}/primary-goals/${goalId}/evidence-links/`, payload).then((res) => res.data),
    remove: (sectionId: number, studentId: number, goalId: number, linkId: number) =>
      apiV2.delete(`/goals/sections/${sectionId}/students/${studentId}/primary-goals/${goalId}/evidence-links/${linkId}/`).then((res) => res.data),
    verify: (
      sectionId: number,
      studentId: number,
      goalId: number,
      linkId: number,
      payload: { status: 'pending' | 'verified' | 'needs_revision'; note?: string },
    ) => apiV2.post(
      `/goals/sections/${sectionId}/students/${studentId}/primary-goals/${goalId}/evidence-links/${linkId}/verification/`,
      payload,
    ).then((res) => res.data),
  },
  externalTasks: {
    list: (params?: { section_id?: number }) => apiV2.get('/goals/my/external-tasks/', { params }).then((res) => res.data),
    create: (payload: {
      section_id: number;
      template_id?: number;
      title?: string;
      source_app?: string;
      due_date?: string | null;
      readiness_tags?: string[];
    }) => apiV2.post('/goals/my/external-tasks/', payload).then((res) => res.data),
    update: (payload: {
      section_id: number;
      entry_id: number;
      title?: string;
      source_app?: string;
      due_date?: string | null;
      status?: 'not_started' | 'in_progress' | 'completed';
      evidence_note?: string;
      evidence_link?: string;
      readiness_tags?: string[];
    }) => apiV2.patch('/goals/my/external-tasks/', payload).then((res) => res.data),
  },
  externalWorkBlocks: {
    list: (params?: { section_id?: number }) =>
      apiV2.get('/goals/my/external-work-blocks/', { params }).then((res) => res.data),
    start: (payload: {
      section_id: number;
      external_task_entry_id?: number;
      title?: string;
      source_app?: string;
      planned_minutes?: number;
    }) => apiV2.post('/goals/my/external-work-blocks/', payload).then((res) => res.data),
    update: (payload: {
      section_id: number;
      block_id: number;
      action: 'stop' | 'complete' | 'pause' | 'resume' | 'extend' | 'add_minutes' | 'evidence' | 'request_support';
      minutes_delta?: number;
      evidence_note?: string;
      evidence_link?: string;
    }) => apiV2.patch('/goals/my/external-work-blocks/', payload).then((res) => res.data),
  },
  externalWorkEvents: {
    list: (params: { section_id: number; block_id?: number; limit?: number }) =>
      apiV2.get('/goals/my/external-work-events/', { params }).then((res) => res.data),
    log: (payload: {
      section_id: number;
      block_id: number;
      event_type: 'switch' | 'minutes_adjusted' | 'evidence_note' | 'evidence_link';
      minutes_delta?: number;
      note?: string;
      link?: string;
      external_task_entry_id?: number;
      title?: string;
      source_app?: string;
      planned_minutes?: number;
    }) => apiV2.post('/goals/my/external-work-events/', payload).then((res) => res.data),
  },
  gateStatus: (sectionId: number) => apiV2.get('/goals/my/gate-status/', { params: { section_id: sectionId } }).then((res) => res.data),
  aiAssist: (payload: {
    section_id: number;
    goal_text?: string;
    daily_plan?: {
      track?: string;
      target?: string;
      first_step?: string;
      success_criteria?: string;
      work_item?: Record<string, any> | null;
    };
    goal_builder?: {
      skill?: string;
      action?: string;
      proof?: string;
      first_step?: string;
    };
    first_step_text?: string;
    stage_tag?: string;
    event_id?: number;
    accepted?: boolean | null;
    rationale_text?: string;
  }) => apiV2.post('/goals/my/ai-assist/', payload).then((res) => res.data),
  updateWorkStatus: (sectionId: number, payload: {
    work_status?: 'not_started' | 'in_progress' | 'paused' | 'completed';
    work_started_at?: string;
    current_focus?: {
      key: string;
      label: string;
      title?: string;
      origin?: 'inferred' | 'student' | 'runtime';
    };
  }) => apiV2.patch('/goals/my/work-status/', { section_id: sectionId, ...payload }).then((res) => res.data),
  teacher: {
    sectionDaily: (sectionId: number, params?: { date?: string }) =>
      apiV2.get(`/goals/sections/${sectionId}/daily/`, { params }).then((res) => res.data),
    sectionMissingQueue: (
      sectionId: number,
      params?: { date?: string; limit?: number },
    ) => apiV2.get(`/goals/sections/${sectionId}/missing-queue/`, { params }).then((res) => res.data),
    sectionSummary: (sectionId: number, params?: { from?: string; to?: string }) =>
      apiV2.get(`/goals/sections/${sectionId}/summary/`, { params }).then((res) => res.data),
    studentDaily: (studentId: number, params: { section_id: number; date?: string }) =>
      apiV2.get(`/goals/students/${studentId}/daily/`, { params }).then((res) => res.data),
    externalWorkSupport: (
      sectionId: number,
      studentId: number,
      payload: { block_id: number; action: 'acknowledge' | 'resolve' },
    ) => apiV2.patch(
      `/goals/sections/${sectionId}/students/${studentId}/external-work-support/`,
      payload,
    ).then((res) => res.data),
    riskAssist: (studentId: number, payload: { section_id: number; date?: string }) =>
      apiV2.post(`/goals/students/${studentId}/risk-assist/`, payload).then((res) => res.data),
    studentMissingQueue: (
      studentId: number,
      params: { section_id: number; date?: string; limit?: number },
    ) => apiV2.get(`/goals/students/${studentId}/missing-queue/`, { params }).then((res) => res.data),
    comment: (payload: { goal_id?: number; cycle_id?: number; text: string; is_flag?: boolean }) =>
      apiV2.post('/goals/comments/', payload).then((res) => res.data),
    bypass: (studentId: number, payload: { section_id: number; reason: string }) =>
      apiV2.post(`/goals/students/${studentId}/bypass/`, payload).then((res) => res.data),
    savePrimaryGoalSupport: (sectionId: number, studentId: number, payload: {
      goal_id?: number;
      support_mode: 'adapted' | 'teacher_stated';
      student_visible_rationale: string;
      goal_track?: 'required_pathway' | 'personal_project';
      goal_purpose?: 'technical_cte' | 'personal_professional';
      title?: string;
      why_text?: string;
      target_metric_text?: string;
    }) => apiV2.post(`/goals/sections/${sectionId}/students/${studentId}/primary-goal-support/`, payload).then((res) => res.data),
    saveDailyPlanSupport: (sectionId: number, studentId: number, payload: {
      date: string;
      support_mode: 'adapted' | 'teacher_stated';
      student_visible_rationale: string;
      plan_track?: string;
      plan_target?: string;
      plan_first_step?: string;
      plan_success_criteria?: string;
      protected_goal_minutes?: number | null;
      planned_secondary_work?: string;
      planned_work_mode?: string;
    }) => apiV2.post(`/goals/sections/${sectionId}/students/${studentId}/daily-plan-support/`, payload).then((res) => res.data),
    reviewGoal: (sectionId: number, studentId: number, goalId: number, payload: {
      decision: 'approved' | 'needs_revision';
      note: string;
      definition_revision: number;
      definition_digest: string;
    }) => apiV2.post(`/goals/sections/${sectionId}/students/${studentId}/primary-goals/${goalId}/teacher-review/`, payload).then((res) => res.data),
    qualityOverride: (sectionId: number, studentId: number, goalId: number, payload: {
      reason_code: 'exploratory_goal' | 'accommodation' | 'teacher_directed_project' | 'unusual_instructional_circumstance' | 'other';
      note?: string;
    }) => apiV2.post(`/goals/sections/${sectionId}/students/${studentId}/primary-goals/${goalId}/quality-override/`, payload).then((res) => res.data),
    standardCandidates: (sectionId: number, studentId: number, goalId: number) =>
      apiV2.get(`/goals/sections/${sectionId}/students/${studentId}/primary-goals/${goalId}/standard-candidates/`).then((res) => res.data),
    standardConfirmation: (sectionId: number, studentId: number, goalId: number, payload: {
      match_evaluation_id: number;
      outcome: 'standard_confirmed' | 'no_defensible_standard';
      standard_id?: number;
      note?: string;
      definition_revision: number;
      definition_digest: string;
    }) => apiV2.post(`/goals/sections/${sectionId}/students/${studentId}/primary-goals/${goalId}/standard-confirmation/`, payload).then((res) => res.data),
    schedule: {
      get: (sectionId: number) => apiV2.get(`/goals/sections/${sectionId}/schedule/`).then((res) => res.data),
      update: (sectionId: number, payload: {
        schedule_type?: 'traditional_5_day' | '4_day_week';
        goal_days?: number[];
        holiday_dates?: string[];
        observed_holidays?: string[];
        adjusted_school_friday_enabled?: boolean;
        makeup_friday_enabled?: boolean;
      }) => apiV2.patch(`/goals/sections/${sectionId}/schedule/`, payload).then((res) => res.data),
    },
    holidays: {
      list: (sectionId: number) => apiV2.get(`/goals/sections/${sectionId}/holidays/`).then((res) => res.data),
      add: (sectionId: number, dates: string[]) =>
        apiV2.post(`/goals/sections/${sectionId}/holidays/`, { dates }).then((res) => res.data),
      observe: (sectionId: number, date: string) =>
        apiV2.post(`/goals/sections/${sectionId}/holidays/${date}/observe`).then((res) => res.data),
      unobserve: (sectionId: number, date: string) =>
        apiV2.delete(`/goals/sections/${sectionId}/holidays/${date}/observe`).then((res) => res.data),
      remove: (sectionId: number, date: string) =>
        apiV2.delete(`/goals/sections/${sectionId}/holidays/${date}/`).then((res) => res.data),
    },
    schoolDays: (sectionId: number, params: { from_date: string; to_date: string }) =>
      apiV2.get(`/goals/sections/${sectionId}/school-days/`, { params }).then((res) => res.data),
    templates: {
      list: (sectionId: number) => apiV2.get(`/goals/sections/${sectionId}/external-templates/`).then((res) => res.data),
      create: (
        sectionId: number,
        payload: {
          title: string;
          source_app?: string;
          expectation_text?: string;
          due_date?: string | null;
          readiness_tags?: string[];
          is_active?: boolean;
        },
      ) => apiV2.post(`/goals/sections/${sectionId}/external-templates/`, payload).then((res) => res.data),
      update: (
        sectionId: number,
        templateId: number,
        payload: {
          title?: string;
          source_app?: string;
          expectation_text?: string;
          due_date?: string | null;
          readiness_tags?: string[];
          is_active?: boolean;
        },
      ) => apiV2.patch(`/goals/sections/${sectionId}/external-templates/${templateId}/`, payload).then((res) => res.data),
      remove: (sectionId: number, templateId: number) =>
        apiV2.delete(`/goals/sections/${sectionId}/external-templates/${templateId}/`).then((res) => res.data),
    },
  },
};

export const performanceReviewApi = {
  pacingPreview: (sectionId: number, studentId: number, cycleId: number, payload: object) =>
    apiV2.post(`/performance-reviews/sections/${sectionId}/students/${studentId}/cycles/${cycleId}/pacing-preview/`, payload).then(res => res.data),
  localDraftStatus: (sectionId: number, studentId: number, cycleId: number, reviewKind?: 'midpoint' | 'final') =>
    apiV2.get(`/performance-reviews/sections/${sectionId}/students/${studentId}/cycles/${cycleId}/local-draft/`, { params: { review_kind: reviewKind } }).then(res => res.data),
  localDraft: async (sectionId: number, studentId: number, cycleId: number, payload: object, reviewKind?: 'midpoint' | 'final') => {
    const path = `/performance-reviews/sections/${sectionId}/students/${studentId}/cycles/${cycleId}/local-draft/`;
    const wholeReview = Boolean((payload as { whole_review?: boolean }).whole_review);
    let result = await apiV2.post(path, wholeReview ? payload : { ...payload, background: true }, {
      timeout: wholeReview ? 240000 : 15000, params: { review_kind: reviewKind },
    });
    const jobId = result.data?.job_id;
    const deadline = Date.now() + 150000;
    while (result.status === 202) {
      if (!jobId || Date.now() >= deadline) {
        throw new Error('The local draft did not finish in time. Nothing was changed or saved.');
      }
      await new Promise(resolve => setTimeout(resolve, 2000));
      result = await apiV2.get(path, {
        timeout: 15000, params: { review_kind: reviewKind, job_id: jobId },
      });
    }
    return result.data;
  },
  my: (goalId: number, reviewKind?: 'midpoint' | 'final') =>
    apiV2.get(`/performance-reviews/my/goals/${goalId}/`, { params: { review_kind: reviewKind } }).then((res) => res.data),
  saveMySelfReview: (goalId: number, payload: Record<string, unknown>, reviewKind?: 'midpoint' | 'final') =>
    apiV2.put(`/performance-reviews/my/goals/${goalId}/`, payload, { params: { review_kind: reviewKind } }).then((res) => res.data),
  teacher: (sectionId: number, studentId: number, cycleId: number, reviewKind?: 'midpoint' | 'final') =>
    apiV2.get(`/performance-reviews/sections/${sectionId}/students/${studentId}/cycles/${cycleId}/`, { params: { review_kind: reviewKind } }).then((res) => res.data),
  saveDraft: (sectionId: number, studentId: number, cycleId: number, payload: {
    technical_performance_summary?: string;
    technical_determination?: string;
    evidence_verification_summary?: string;
    nsr_growth_summary?: string;
    next_technical_priority?: string;
    next_nsr_direction?: string;
    pathway_profile_key?: string;
    goal_outcome?: string;
    goal_outcome_note?: string;
    technical_evidence_status?: string;
    performance_dimensions?: object;
    next_block_plan?: object;
    overall_summary?: string;
    industry_feedback?: string;
    teacher_private_note?: string;
  }, reviewKind?: 'midpoint' | 'final') => apiV2.put(`/performance-reviews/sections/${sectionId}/students/${studentId}/cycles/${cycleId}/`, payload, { params: { review_kind: reviewKind } }).then((res) => res.data),
  finalize: (sectionId: number, studentId: number, cycleId: number, finalizationToken: string, reviewKind?: 'midpoint' | 'final') =>
    apiV2.post(`/performance-reviews/sections/${sectionId}/students/${studentId}/cycles/${cycleId}/finalize/`, {
      finalization_token: finalizationToken,
    }, { params: { review_kind: reviewKind } }).then((res) => res.data),
  requirements: (sectionId: number, cycleId: number) =>
    apiV2.get(`/performance-reviews/sections/${sectionId}/cycles/${cycleId}/exceptions/`).then((res) => res.data),
  approveException: (sectionId: number, cycleId: number, payload: {
    student: number;
    exception_type: 'withdrawal_section_change' | 'extended_absence' | 'incomplete_participation' | 'documented_exception';
    reason: string;
    approval_token: string;
  }) => apiV2.post(`/performance-reviews/sections/${sectionId}/cycles/${cycleId}/exceptions/`, payload).then((res) => res.data),
  revokeException: (sectionId: number, cycleId: number, exceptionId: number) =>
    apiV2.post(`/performance-reviews/sections/${sectionId}/cycles/${cycleId}/exceptions/${exceptionId}/revoke/`).then((res) => res.data),
};

export const coachingApi = {
  progress: (sectionId: number, cycleId: number, studentId?: number, checkInId?: number) =>
    apiV2.get(studentId ? `/coaching/sections/${sectionId}/students/${studentId}/progress/` : `/coaching/my/sections/${sectionId}/progress/`, {
      params: { cycle_id: cycleId, check_in_id: checkInId },
    }).then((res) => res.data),
  policy: (sectionId: number) =>
    apiV2.get(`/coaching/sections/${sectionId}/policy/`).then((res) => res.data),
  savePolicy: (sectionId: number, payload: { preset_key: string }) =>
    apiV2.put(`/coaching/sections/${sectionId}/policy/`, payload).then((res) => res.data),
  overview: (sectionId: number, cycleId?: number) =>
    apiV2.get(`/coaching/sections/${sectionId}/overview/`, {
      params: cycleId ? { cycle_id: cycleId } : undefined,
    }).then((res) => res.data),
  studentRecord: (sectionId: number, studentId: number, cycleId?: number) =>
    apiV2.get(`/coaching/sections/${sectionId}/students/${studentId}/`, {
      params: cycleId ? { cycle_id: cycleId } : undefined,
    }).then((res) => res.data),
  createCheckIn: (sectionId: number, studentId: number, payload: { instructional_cycle: number; purpose?: string; target_date?: string }) =>
    apiV2.post(`/coaching/sections/${sectionId}/students/${studentId}/check-ins/`, payload).then((res) => res.data),
  conference: (sectionId: number, studentId: number, checkInId: number) =>
    apiV2.get(`/coaching/sections/${sectionId}/students/${studentId}/check-ins/${checkInId}/`).then((res) => res.data),
  completeConference: (sectionId: number, studentId: number, checkInId: number, payload: Record<string, unknown>) =>
    apiV2.put(`/coaching/sections/${sectionId}/students/${studentId}/check-ins/${checkInId}/`, payload).then((res) => res.data),
  updateFollowThrough: (sectionId: number, studentId: number, checkInId: number, payload: Record<string, unknown>) =>
    apiV2.patch(`/coaching/sections/${sectionId}/students/${studentId}/check-ins/${checkInId}/follow-through/`, payload).then((res) => res.data),
  excuse: (sectionId: number, studentId: number, checkInId: number, reason: string) =>
    apiV2.post(`/coaching/sections/${sectionId}/students/${studentId}/check-ins/${checkInId}/excuse/`, { reason }).then((res) => res.data),
  my: (sectionId: number) => apiV2.get('/coaching/my/', { params: { section_id: sectionId } }).then((res) => res.data),
  prepare: (checkInId: number, payload: Record<string, unknown>) =>
    apiV2.put(`/coaching/my/check-ins/${checkInId}/preparation/`, payload).then((res) => res.data),
};

export const nsrApi = {
  rubric: () => apiV2.get('/nsr/rubric/').then((res) => res.data),
  myCycle: (cycleId: number) => apiV2.get(`/nsr/my/cycles/${cycleId}/`).then((res) => res.data),
  saveMyFocus: (cycleId: number, payload: { primary_item_key: string; secondary_item_key?: string }) =>
    apiV2.put(`/nsr/my/cycles/${cycleId}/focus/`, payload).then((res) => res.data),
  saveMyReflection: (cycleId: number, studentReflection: string) =>
    apiV2.patch(`/nsr/my/cycles/${cycleId}/reflection/`, { student_reflection: studentReflection }).then((res) => res.data),
  teacherCycle: (sectionId: number, studentId: number, cycleId: number) =>
    apiV2.get(`/nsr/sections/${sectionId}/students/${studentId}/cycles/${cycleId}/`).then((res) => res.data),
  saveTeacherFocus: (
    sectionId: number,
    studentId: number,
    cycleId: number,
    payload: { primary_item_key: string; secondary_item_key?: string },
  ) => apiV2.put(`/nsr/sections/${sectionId}/students/${studentId}/cycles/${cycleId}/focus/`, payload).then((res) => res.data),
  createObservation: (
    sectionId: number,
    studentId: number,
    cycleId: number,
    payload: {
      rubric_item_key: string;
      observation_text: string;
      observed_on?: string;
      student_visible?: boolean;
      include_in_external_reports?: boolean;
    },
  ) => apiV2.post(`/nsr/sections/${sectionId}/students/${studentId}/cycles/${cycleId}/observations/`, payload).then((res) => res.data),
  updateObservation: (
    sectionId: number,
    studentId: number,
    cycleId: number,
    observationId: number,
    payload: Record<string, unknown>,
  ) => apiV2.patch(`/nsr/sections/${sectionId}/students/${studentId}/cycles/${cycleId}/observations/${observationId}/`, payload).then((res) => res.data),
  removeObservation: (sectionId: number, studentId: number, cycleId: number, observationId: number) =>
    apiV2.delete(`/nsr/sections/${sectionId}/students/${studentId}/cycles/${cycleId}/observations/${observationId}/`),
};

export default apiV2;
